<template>
  <BittsCard
    class="c-billing-plan-card"
    :class="{
      'w-1/2': !isBillingPage,
      'h-full min-h-[800px]': isBillingPage,
      'c-billing-plan-card__connector': isConnectorTier,
      'c-billing-plan-card__supernode': isSupernodeTier,
      'c-billing-plan-card__enterprise': isEnterpriseTier,
    }"
  >
    <div class="flex flex-col p-24 gap-24">
      <div class="flex flex-col gap-8">
        <div>
          <div class="pb-8 flex gap-8 items-center text-xl">
            <p class="font-bold c-billing-plan-card__tier-title">{{
              tierName
            }}</p>
            <BittsTag
              v-if="isSupernodeTier || (isCurrentTier && isConnectorTier)"
              color="neutral"
              size="x-small"
              class="flex items-center"
              :class="{
                'c-billing-plan-card__recommended-tag': isSupernodeTier,
              }"
            >
              <template v-if="isSupernodeTier" #pre>
                <FontAwesomeIcon
                  class="mr-4 h-12 w-12 text-white!"
                  :icon="['fak', 'star']"
                />
              </template>
              <span :class="{ 'text-white!': isSupernodeTier }">{{
                isSupernodeTier ? 'Recommended' : 'Current plan'
              }}</span>
            </BittsTag>
            <component
              v-else-if="isConnectorTier"
              :is="annualPricing ? 'div' : BittsTooltip"
              class="ml-auto"
            >
              <BittsSwitch
                v-model="annualPricing"
                :label="annualPricing ? 'Pay annually' : 'Pay monthly*'"
                :use-text="false"
                class="ml-auto"
              />
              <template #title> Requires annual commitment </template>
            </component>
          </div>
          <p class="text-neutral-text-weak text-base h-[63px]">
            {{ description }}
          </p>
        </div>
        <div>
          <p
            :class="{
              'mb-16':
                onLegacyConnector && !showSalesSeatPricing && !hasNewPricing,
            }"
            class="text-neutral-text-strong font-bold text-xxl flex items-end"
          >
            {{ corePrice }}
            <span
              v-if="isConnectorTier && onLegacyConnector"
              class="text-neutral-text-weak text-base font-normal"
            >
              per seat/month
            </span>
            <span
              v-else-if="isConnectorTier || isFreeTier"
              class="text-neutral-text-weak text-base font-normal flex gap-2 items-center"
            >
              {{ isFreeTier ? '/month' : connectorTierPricingText }}
              <span
                v-if="isConnectorTier && annualPricing"
                class="text-success-text whitespace-nowrap"
                :style="{ display: 'contents' }"
                >(save 10%)</span
              >
            </span>
          </p>
          <template v-if="hasNewPricing">
            <p class="text-neutral-text-weak c-billing-plan-card__seats-text">
              {{ newPricingSubtitle.seats }}
            </p>
            <p
              v-if="hasMcpMeteringUi"
              class="text-neutral-text-weak c-billing-plan-card__seats-text flex items-center gap-4"
            >
              <BittsTooltip v-if="showCreditsLock">
                <FontAwesomeIcon :icon="['fak', 'lock']" class="h-8 w-8" />
                <template #title> Locked platform feature </template>
              </BittsTooltip>
              {{ newPricingSubtitle.credits }}
            </p>
          </template>
          <template v-else>
            <p v-if="isSupernodeTier || isEnterpriseTier" class="h-[15px]" />
            <p
              v-else-if="
                isConnectorTier && onLegacyConnector && showSalesSeatPricing
              "
              class="text-success-text"
              :class="onLegacyConnector ? 'h-[15px]' : 'h-[21px]'"
            >
              or {{ salesPrice }} per seat/month for Sales seats
            </p>
            <p
              v-else-if="isConnectorTier && !onLegacyConnector"
              class="c-billing-plan-card__seats-text"
            >
              Includes 1 Full Access seat
            </p>
            <p
              v-else-if="isFreeTier"
              class="text-neutral-text-weak c-billing-plan-card__seats-text"
            >
              includes 3 Full Access seats
            </p>
          </template>
        </div>
      </div>

      <BittsButton
        v-if="!isBillingPage"
        :text="buttonText"
        :type="buttonType"
        size="medium"
        :left-icon="isSupernodeTier ? ['fak', 'upgrade'] : undefined"
        class="self-start"
        @click="handleButtonClick"
      />
      <div v-else>
        <slot name="action" />
      </div>

      <BillingPlanComparisonFeatureList
        :tier="String(tier)"
        :comparison-modal="true"
        :highlight
      />
      <div v-if="isBillingPage">
        <div class="c-billing-plan-comparison-card__integrations">
          <BittsTag
            v-for="item in filteredIntegrations"
            :key="item.text"
            color="partner"
            size="small"
            class="c-billing-plan-comparison-card__integration-tag"
          >
            <template #pre>
              <BittsSvg
                v-if="item.svg"
                class="w-12 h-12 mr-8"
                :svg="item.svg"
              />
            </template>
            <span>
              {{ item.text }}
            </span>
          </BittsTag>
        </div>
      </div>
    </div>
  </BittsCard>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsCard,
  BittsSvg,
  BittsSwitch,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';

import { capitalize } from 'lodash';
import { computed, ref } from 'vue';

import BillingPlanComparisonFeatureList from '@/components/billing/BillingPlanComparisonFeatureList.vue';

import useAuth from '@/composables/useAuth';
import useSeats from '@/composables/useSeats';
import {
  BILLING_IMPROVEMENTS_COPY,
  FULL_ACCESS,
  SALES_ONLY,
  SEAT_COSTS,
  V5_BILLING_COPY,
} from '@/constants/billing';
import {
  HS3_DATA_SOURCE_TYPE,
  HUBSPOT_DATA_SOURCE_TYPE,
  SALESFORCE_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useFeatureFlagStore } from '@/stores';
import { BillingPeriod } from '@/types/billing';
import { centsToDollars } from '@/utils';

interface Props {
  tier: keyof typeof V5_BILLING_COPY.explorePlanCards;
  isCurrentTier?: boolean;
  period?: BillingPeriod;
  highlight?: string;
  isBillingPage?: boolean;
  onLegacyConnector?: boolean;
}

const {
  tier,
  isCurrentTier = false,
  highlight = '',
  period = BillingPeriod.Month,
  isBillingPage = false,
  onLegacyConnector = false,
} = defineProps<Props>();

const emit = defineEmits<(e: 'action') => void>();

const { currentOrg } = useAuth();
const { blockSalesSeatsPPV5 } = useSeats();
const { hasFeatureFlag } = useFeatureFlagStore();
const hasNewPricing = computed(() => hasFeatureFlag(FEATURE_FLAG.TEMP_PPV67));
const hasMcpMeteringUi = computed(() =>
  hasFeatureFlag(FEATURE_FLAG.TEMP_MCP_METERING_UI),
);

const tierCopy = computed(() => BILLING_IMPROVEMENTS_COPY[tier as string]);
const description = computed(() => {
  const copy = tierCopy.value;
  if (!copy) return '';
  return hasNewPricing.value
    ? (copy.descriptionNewPricing ?? copy.description)
    : copy.description;
});

const newPricingSubtitle = computed<{ seats: string; credits: string }>(() => {
  switch (tier) {
    case 'explorer':
      return {
        seats: 'Includes 3 Full Access seats',
        credits: 'Includes 50 Crossbeam Credits',
      };
    case 'connector':
      return {
        seats: 'Includes 1 Full Access seat',
        credits: onLegacyConnector
          ? '500 Crossbeam Credits'
          : 'Includes 500 Crossbeam Credits',
      };
    case 'supernode':
      return {
        seats: 'Includes 3 Full Access seats',
        credits: 'Includes 2,500 Crossbeam Credits',
      };
    case 'enterprise':
      return {
        seats: 'Full Access seats: Custom',
        credits: 'Includes 5,000 Crossbeam Credits',
      };
    default:
      return { seats: '', credits: '' };
  }
});
const isConnectorTier = computed(() => tier === 'connector');
const isSupernodeTier = computed(() => tier === 'supernode');
const isEnterpriseTier = computed(() => tier === 'enterprise');
const isFreeTier = computed(() => tier === 'explorer');

const showSalesSeatPricing = computed(
  () => onLegacyConnector && !blockSalesSeatsPPV5.value,
);

const showCreditsLock = computed(
  () => isConnectorTier.value && onLegacyConnector,
);

const tierName = computed(() => {
  if (!isFreeTier.value) return capitalize(tier as string);
  return 'Free';
});

const annualPricing = ref(true);
const connectorTierPricingText = computed(() => {
  return annualPricing.value
    ? '/year, billed annually'
    : '/month, billed monthly';
});

const buttonText = computed(() => {
  if (isSupernodeTier.value) return 'Talk to Sales';
  return 'Upgrade to Connector';
});

const buttonType = computed(() => {
  if (isSupernodeTier.value) return 'upsell';
  return 'primary';
});

const corePrice = computed(() => {
  if (isSupernodeTier.value || isEnterpriseTier.value) return 'Custom';
  if (isFreeTier.value) return '$0';
  if (isConnectorTier.value && !onLegacyConnector) {
    return annualPricing.value ? '$4800' : '$440';
  }
  return (
    period &&
    centsToDollars(
      SEAT_COSTS[FULL_ACCESS][period] / (period === 'year' ? 12 : 1),
    )
  );
});

const salesPrice = computed(() => {
  if (isSupernodeTier.value) return null;
  return (
    period &&
    centsToDollars(
      SEAT_COSTS[SALES_ONLY][period] / (period === 'year' ? 12 : 1),
    )
  );
});

const preferredCrm = computed(() => currentOrg.value.preferred_crm);
const filteredIntegrations = computed(() => {
  return V5_BILLING_COPY.explorePlanCards[tier].features.integrations.filter(
    (integration: { svg?: string; text: string }) => {
      if (integration.svg?.startsWith(SALESFORCE_DATA_SOURCE_TYPE))
        return preferredCrm.value === SALESFORCE_DATA_SOURCE_TYPE;
      if (integration.svg?.startsWith(HUBSPOT_DATA_SOURCE_TYPE))
        return preferredCrm.value === HS3_DATA_SOURCE_TYPE;
      return true;
    },
  );
});

function handleButtonClick() {
  emit('action');
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-billing-plan-card {
  @apply relative border-neutral-border bg-white;
  &.c-billing-plan-card__connector {
    .c-billing-plan-card__tier-title {
      background: linear-gradient(
        90deg,
        theme(colors.info-accent) 0.11%,
        color-mix(in srgb, theme(colors.info-accent) 75%, transparent) 27.41%
      );
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
  &.c-billing-plan-card__supernode {
    .c-billing-plan-card__highlight-tag {
      @apply w-full left-24 absolute top-[-12px] justify-start;
    }
    .c-billing-plan-card__highlight-tag__billing {
      @apply w-full left-0 absolute top-[-12px] flex justify-center;
    }
    .c-billing-plan-card__tier-title {
      background: linear-gradient(
        90deg,
        theme(colors.secondary-accent) 0.13%,
        color-mix(in srgb, theme(colors.beta-text) 75%, transparent) 24.57%
      );
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
  &.c-billing-plan-card__enterprise {
    .c-billing-plan-card__tier-title {
      background: linear-gradient(
        90deg,
        theme(colors.blue-700) 0.13%,
        theme(colors.primary-accent) 24.57%
      );
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
  .c-billing-plan-comparison-card__integrations {
    @apply flex flex-col items-start gap-12;
    div {
      @apply w-auto shrink;
    }
  }
}
</style>
<style lang="pcss">
@reference "../../css/theme.css";
.c-billing-plan-comparison-card__integration-tag.bitts-tag {
  .ant-tag {
    @apply cursor-pointer flex items-center;
  }
}
.c-billing-plan-card__seats-text {
  @apply text-neutral-text-weak text-xs;
}
.c-billing-plan-card__recommended-tag.bitts-tag .ant-tag {
  @apply rounded-full! text-white!;
  background: linear-gradient(
    90deg,
    theme(colors.secondary-accent) 0.13%,
    theme(colors.beta-text) 99.87%
  ) !important;
}
</style>
